import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../shared/services/authentication.service';
import { LoginRequest } from '../shared/models/user';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroying$ = new Subject<void>();
  // When we arrive here from an explicit logout, stay on the login screen and
  // suppress the silent SSO re-auth that checkAccounts() would otherwise do.
  private loggedOut = false;
  isLoading = false;
  errorText = '';

  constructor(
    private auth: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  ngOnInit(): void {
    if (!environment.production && globalThis.location.hostname === 'localhost') {
      this.router.navigate(['/agents']);
      return;
    }

    if (this.route.snapshot.queryParamMap.get('expired') === 'true') {
      this.errorText = 'Your session has expired. Please sign in again.';
    }

    // Arrived from a logout (e.g. the Studio app). Set the guard synchronously
    // so the inProgress$/msalSubject$ subscriptions below can't auto-login
    // before we finish tearing the session down.
    this.loggedOut = this.route.snapshot.queryParamMap.get('logout') === 'true';

    this.msalService.handleRedirectObservable().pipe(takeUntil(this.destroying$)).subscribe({
      next: () => {
        if (!this.loggedOut) return;
        sessionStorage.clear();
        // Local sign-out: drops the cached MSAL account so the next page load
        // won't silently re-auth. Does NOT redirect to Azure AD / end the SSO
        // session, so the user can sign back in without a credential prompt.
        this.msalService.instance.clearCache().catch(() => {});
        // Drop the ?logout flag so a refresh doesn't repeat the teardown.
        this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
      },
      error: () => {}
    });
    this.msalBroadcastService.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      takeUntil(this.destroying$)
    ).subscribe(() => this.checkAccounts());

    this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this.destroying$)
    ).subscribe(() => this.checkAccounts());
  }

  onLogin() {
    this.errorText = '';
    this.isLoading = true;
    this.auth.loginSso();
  }

  private checkAccounts() {
    if (this.loggedOut) { this.isLoading = false; return; }
    const accounts = this.msalService.instance.getAllAccounts();
    if (!accounts.length) { this.isLoading = false; return; }
    this.isLoading = true;
    const account = accounts[0];
    this.msalService.instance.setActiveAccount(account);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.msalService.acquireTokenSilent({ scopes: ['user.read'], account } as any).subscribe({
      next: (tokenResponse) => {
        if (!tokenResponse?.accessToken || !tokenResponse?.idToken) { this.isLoading = false; return; }
        const expiry = new Date(Date.now() + 3600 * 1000);
        sessionStorage.setItem('msal-access-token', JSON.stringify({ token: tokenResponse.accessToken, expiry }));
        sessionStorage.setItem('msal-id-token', JSON.stringify({ token: tokenResponse.idToken, expiry }));
        const req: LoginRequest = { accessToken: tokenResponse.accessToken, idToken: tokenResponse.idToken, email: account.username ?? '' };
        this.auth.login(req).subscribe({
          next: () => this.router.navigate(['/agents']),
          error: (err) => { this.isLoading = false; this.errorText = this.extractError(err); }
        });
      },
      error: (err) => { this.isLoading = false; this.errorText = this.extractError(err); }
    });
  }

  private extractError(err: unknown): string {
    if (!err) return 'Sign-in failed. Please try again.';
    if (typeof err === 'string') return err;
    const e = err as { error?: { message?: string }; message?: string };
    return e.error?.message || e.message || 'Sign-in failed. Please try again.';
  }

  ngOnDestroy(): void { this.destroying$.next(); this.destroying$.complete(); }
}
